import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, CheckCircle2, XCircle, Pause, Play, Square, RotateCcw, AlertTriangle, BarChart3, Eye, MousePointer, Target, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api, { formatPaise } from '@/lib/api';
import { toast } from 'sonner';
import PhonePreview from '@/components/PhonePreview';
import MetaSuggestions from '@/components/MetaSuggestions';
import AdMetrics from '@/components/AdMetrics';
import CustomerFeedback from '@/components/CustomerFeedback';
import OptimizerFeed from '@/components/OptimizerFeed';
import ExtendAdDialog from '@/components/ExtendAdDialog';
import { goalLabel, statusOf } from '@/lib/campaign';
import { useAuth } from '@/contexts/AuthContext';

const NOTICES = {
  pending_review: "Our team checks every customer's first ad before it goes to Meta. This usually takes a few hours.",
  queued: 'Your ad is in line to be sent to Meta. This happens within a few minutes.',
  creating: 'Your ad is being set up on Meta right now.',
  in_meta_review: 'Meta is reviewing your ad. This usually takes a few hours, sometimes up to a day.',
};

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const { user } = useAuth();
  // Replies to actions don't carry the benchmark (only the page load does): keep the last one.
  const update = (data) => setCampaign(c => ({ ...data, benchmark: data.benchmark ?? c?.benchmark }));

  useEffect(() => {
    api.get(`/api/campaigns/${campaignId}`).then(({ data }) => {
      if (['draft', 'generating', 'ai_question', 'review'].includes(data.status)) navigate(`/create-ad/${data.id}`, { replace: true });
      else setCampaign(data);
    }).catch(() => navigate('/dashboard')).finally(() => setLoading(false));
  }, [campaignId, navigate]);

  // Keep the status fresh while Meta is working on it.
  useEffect(() => {
    if (!campaign || !['pending_review', 'queued', 'creating', 'in_meta_review', 'live'].includes(campaign.status)) return undefined;
    const t = setInterval(() => api.get(`/api/campaigns/${campaignId}`).then(({ data }) => setCampaign(data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [campaign, campaignId]);

  const handleAction = async (action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/api/campaigns/${campaignId}/${action}`);
      update(data);
      toast.success({ pause: 'Ad paused', resume: 'Ad resumed', end: 'Done' }[action]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setActionLoading(false);
  };

  const runAgain = async () => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/api/campaigns/${campaignId}/rerun`);
      navigate(`/create-ad/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set up the ad again');
      setActionLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;
  if (!campaign) return null;

  const status = statusOf(campaign.status);
  const variant = campaign.variants?.find(v => v.id === campaign.selected_variant_id) || campaign.variants?.[0];
  const cost = campaign.cost_breakdown;
  const perf = campaign.performance || {};
  const settlement = campaign.settlement;
  const onMeta = !!campaign.meta_campaign_id;
  const canPause = ['live', 'in_meta_review'].includes(campaign.status);
  const canResume = campaign.status === 'paused';
  const canCancel = ['pending_review', 'queued'].includes(campaign.status);
  const canEnd = ['in_meta_review', 'live', 'paused'].includes(campaign.status);
  const finished = ['ended', 'rejected', 'cancelled'].includes(campaign.status);
  const canRerun = ['ended', 'cancelled'].includes(campaign.status) && !!campaign.selected_variant_id;
  const canExtend = ['live', 'paused'].includes(campaign.status) && !!campaign.end_at;

  return (
    <div data-testid="campaign-detail-page">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/dashboard')}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight font-heading mb-1 break-words">
            {campaign.brief?.promotion || 'Campaign'}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
            {campaign.brief?.goal && <span className="text-xs text-muted-foreground">{goalLabel(campaign.brief.goal)}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
          {canRerun && (
            <Button size="sm" className="gradient-bg" onClick={runAgain} disabled={actionLoading} data-testid="rerun-campaign-btn">
              <RotateCcw className="w-3 h-3" /> Run again
            </Button>
          )}
          {canExtend && (
            <Button size="sm" className="gradient-bg" onClick={() => setExtendOpen(true)} disabled={actionLoading} data-testid="extend-campaign-btn">
              <TrendingUp className="w-3 h-3 mr-1" /> Add budget or days
            </Button>
          )}
          {canPause && (
            <Button size="sm" variant="outline" onClick={() => handleAction('pause', 'Pause this ad? It stops showing until you resume it.')} disabled={actionLoading}>
              <Pause className="w-3 h-3 mr-1" /> Pause
            </Button>
          )}
          {canResume && (
            <Button size="sm" className="gradient-bg" onClick={() => handleAction('resume')} disabled={actionLoading}>
              <Play className="w-3 h-3 mr-1" /> Resume
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="destructive" onClick={() => handleAction('end', 'Cancel this ad? The money held for it comes straight back to your wallet.')} disabled={actionLoading} data-testid="cancel-campaign-btn">
              <Square className="w-3 h-3 mr-1" /> Cancel
            </Button>
          )}
          {canEnd && (
            <Button size="sm" variant="destructive" onClick={() => handleAction('end', 'End this ad for good? Unspent money comes back to your wallet once Meta sends its final numbers (within 48 hours).')} disabled={actionLoading} data-testid="end-campaign-btn">
              <Square className="w-3 h-3 mr-1" /> End
            </Button>
          )}
        </div>
      </div>

      {/* Rejection notice */}
      {campaign.status === 'rejected' && campaign.rejection_reason && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Campaign Rejected</p>
            <p className="text-xs text-muted-foreground mt-1">{campaign.rejection_reason}</p>
          </div>
        </div>
      )}

      {/* The optimizer's warning: no results after a few days (the customer decides whether to end it) */}
      {campaign.poor_warning && canEnd && (
        <div className="p-4 rounded-xl bg-amber-500/10 border-2 border-amber-500/40 mb-6 flex items-start gap-3" data-testid="poor-ad-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">This ad isn't getting results yet</p>
            <p className="text-xs mt-1">{campaign.poor_warning.message}</p>
            <p className="text-xs text-muted-foreground mt-1"><b className="text-foreground">Tip:</b> {campaign.poor_warning.tip}</p>
            <Button size="sm" variant="destructive" className="mt-2.5" disabled={actionLoading}
              onClick={() => handleAction('end', 'End this ad for good? Unspent money comes back to your wallet once Meta sends its final numbers (within 48 hours).')}
              data-testid="warning-end-btn">
              <Square className="w-3 h-3 mr-1" /> End ad
            </Button>
          </div>
        </div>
      )}

      {/* Progress notices */}
      {NOTICES[campaign.status] && (
        <div className="p-4 rounded-xl bg-butter border-2 border-ink shadow-neo-sm mb-6 flex items-start gap-3">
          <Clock className="w-5 h-5 text-brand-strong shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-brand-strong">{status.label}</p>
            <p className="text-xs text-muted-foreground mt-1">{NOTICES[campaign.status]}</p>
          </div>
        </div>
      )}
      {finished && !campaign.released && onMeta && (
        <div className="p-4 rounded-xl bg-butter/50 border-2 border-ink/25 mb-6 text-xs text-muted-foreground">
          Unspent money comes back to your wallet within 48 hours, once Meta sends its final spend figures.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details */}
        <div className="space-y-4 min-w-0">
          {/* Cost breakdown */}
          {cost && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-card border-2 border-ink">
              <h3 className="text-sm font-semibold mb-3">Cost Breakdown</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Ad Spend</span><span className="text-right">{formatPaise(cost.ad_spend_paise)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Service Fee (15%)</span><span className="text-right">{formatPaise(cost.service_fee_paise)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">GST (18%)</span><span className="text-right">{formatPaise(cost.gst_paise)}</span></div>
                <div className="border-t-2 border-dashed border-ink pt-2 flex justify-between font-bold">
                  <span>Total</span><span className="gradient-text">{formatPaise(cost.total_paise)}</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Campaign info */}
          <div className="p-4 rounded-xl bg-card border-2 border-ink">
            <h3 className="text-sm font-semibold mb-3">Campaign Details</h3>
            <div className="space-y-2 text-sm">
              {campaign.budget_paise > 0 && <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Budget</span><span className="text-right">{campaign.duration_days > 0 ? `${formatPaise(Math.round(campaign.budget_paise / campaign.duration_days))}/day × ${campaign.duration_days} = ` : ''}{formatPaise(campaign.budget_paise)}</span></div>}
              {campaign.duration_days > 0 && <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Duration</span><span className="text-right">{campaign.duration_days} days</span></div>}
              <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Location</span><span className="text-right">{campaign.brief?.location || 'N/A'}</span></div>
              <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Audience</span><span className="text-right">{campaign.brief?.age_min}-{campaign.brief?.age_max}, {campaign.brief?.gender}</span></div>
            </div>
          </div>

          {settlement && (
            <div className="p-4 rounded-xl bg-card border-2 border-ink">
              <h3 className="text-sm font-semibold mb-3">Final settlement</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Spent on Meta</span><span className="text-right">₹{(settlement.ad_spend_paise / 100).toFixed(2)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Fee + GST on that spend</span><span className="text-right">₹{((settlement.service_fee_paise + settlement.gst_paise) / 100).toFixed(2)}</span></div>
                <div className="flex justify-between gap-3 font-bold"><span>Returned to your wallet</span><span className="text-money">₹{(settlement.returned_paise / 100).toFixed(2)}</span></div>
              </div>
            </div>
          )}

          {/* Performance */}
          <div className="p-4 rounded-xl bg-card border-2 border-ink">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" /> Performance
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Reach', icon: Eye, value: campaign.performance?.reach || 0 },
                { label: 'Impressions', icon: Eye, value: campaign.performance?.impressions || 0 },
                { label: 'Clicks', icon: MousePointer, value: campaign.performance?.clicks || 0 },
                { label: 'Results', icon: Target, value: campaign.performance?.results || 0 },
              ].map(m => (
                <div key={m.label} className="p-2.5 rounded-lg bg-butter/50 border-2 border-ink/15 min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{m.label}</p>
                  <p className="text-lg font-bold font-mono break-all">{m.value.toLocaleString()}</p>
                </div>
              ))}
            </div>
            {perf.spend_used_paise > 0 && (
              <p className="text-xs text-muted-foreground mt-3">Spent so far: <b className="text-foreground">₹{(perf.spend_used_paise / 100).toFixed(2)}</b> of {formatPaise(campaign.budget_paise)}</p>
            )}
            {!perf.impressions && (
              <p className="text-xs text-muted-foreground mt-3 text-center">Numbers show up here once your ad starts running.</p>
            )}
          </div>

          <AdMetrics campaign={campaign} />

          <CustomerFeedback campaign={campaign} onChange={update} />

          <OptimizerFeed campaign={campaign} onChange={update} />

          <MetaSuggestions campaign={campaign} onAddBudget={canExtend ? () => setExtendOpen(true) : undefined} />
        </div>

        {/* Preview */}
        {variant && (
          <div className="flex flex-col items-center gap-3 min-w-0">
            <PhonePreview variant={variant} pageName={campaign?.ad_page?.page_name || user?.ad_page?.page_name || user?.business_profile?.business_name || 'Your Business'} />
            {campaign?.video && (
              <div className="w-full max-w-xs p-3 rounded-xl bg-card border-2 border-ink text-sm" data-testid="campaign-video">
                <p className="font-medium">Video ad {campaign.video.shape === 'square' ? '(1:1)' : '(9:16)'}</p>
                <p className="text-xs text-muted-foreground">Runs as its own ad next to your image ad, sharing the same budget.</p>
                {campaign.video_rejection_reason && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Meta didn't approve the video: {campaign.video_rejection_reason} Your image ad keeps running.</p>
                )}
                <video src={`${process.env.REACT_APP_BACKEND_URL}${campaign.video.url}`} controls playsInline className="mt-2 w-full rounded-lg bg-black" />
              </div>
            )}
          </div>
        )}
      </div>
      {canExtend && <ExtendAdDialog campaign={campaign} open={extendOpen} onOpenChange={setExtendOpen} onDone={update} />}
    </div>
  );
}
